import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { sendTelegram } from '@/lib/telegram-sender';

// ─── Types ────────────────────────────────────────────────────

interface TelegramAccount {
  id: number;
  user_email: string;
  name: string;
  chat_id: string;
  bot_token: string;
  notify_types: string[];
  is_active: boolean;
  created_at: string;
}

// ─── Category catalog (shared codes with edge function) ──────

interface CategoryOption {
  code: string;
  label: string;
}

const CATEGORY_OPTIONS: CategoryOption[] = [
  { code: 'tma50-cross', label: 'TMA50 Cross' },
  { code: 'ma-convergence', label: 'MA Convergence (SMA↔TMA50)' },
  { code: 'perfect-convergence', label: 'Perfect MA Convergence' },
  { code: 'box', label: 'Box' },
  { code: 'box-vs-tma50', label: 'Box vs TMA50' },
  { code: 'order-modify', label: 'Order Modify (Open/Close/Sửa lệnh)' },
  { code: 'test', label: 'Test' },
];

const CATEGORY_LABELS: Record<string, string> = CATEGORY_OPTIONS.reduce(
  (acc, c) => ({ ...acc, [c.code]: c.label }),
  {},
);

function maskToken(token: string): string {
  if (!token) return '—';
  const idx = token.indexOf(':');
  if (idx >= 0) return token.slice(0, idx + 6) + '…';
  return token.length > 12 ? token.slice(0, 12) + '…' : token;
}

// ─── Component ────────────────────────────────────────────────

export default function TelegramAccounts() {
  const [accounts, setAccounts] = useState<TelegramAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state (shared between add + edit)
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [chatId, setChatId] = useState('');
  const [botToken, setBotToken] = useState('');
  const [allSelected, setAllSelected] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);
  const [toggleBusy, setToggleBusy] = useState<number | null>(null);
  const [testState, setTestState] = useState<Record<number, { running: boolean; result: string | null }>>({});

  const loadAccounts = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('telegram_accounts')
        .select('*')
        .order('created_at', { ascending: true });

      if (err) throw err;
      setAccounts((data || []) as TelegramAccount[]);
      setError(null);
    } catch (e) {
      const m = e && typeof e === 'object' && 'message' in e ? String((e as Record<string, unknown>).message) : 'Failed to load';
      setError(m);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // ─── Form helpers ──────────────────────────────────────────

  const resetForm = () => {
    setName('');
    setChatId('');
    setBotToken('');
    setAllSelected(true);
    setSelectedTypes([]);
    setMsg(null);
  };

  const openAdd = () => {
    resetForm();
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (acc: TelegramAccount) => {
    setName(acc.name);
    setChatId(acc.chat_id);
    setBotToken(acc.bot_token);
    if (acc.notify_types.includes('all')) {
      setAllSelected(true);
      setSelectedTypes([]);
    } else {
      setAllSelected(false);
      setSelectedTypes(acc.notify_types);
    }
    setEditingId(acc.id);
    setFormOpen(true);
    setMsg(null);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    resetForm();
  };

  const toggleAll = () => {
    if (allSelected) {
      setAllSelected(false);
    } else {
      setAllSelected(true);
      setSelectedTypes([]);
    }
  };

  const toggleCategory = (code: string) => {
    setAllSelected(false);
    setSelectedTypes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  // ─── Save (add or edit) ────────────────────────────────────

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);

    if (!name.trim() || !chatId.trim() || !botToken.trim()) {
      setMsg({ type: 'error', text: 'Vui lòng điền đầy đủ Tên, Chat ID và Bot Token' });
      return;
    }

    const notifyTypes = allSelected ? ['all'] : selectedTypes;
    if (!allSelected && notifyTypes.length === 0) {
      setMsg({ type: 'error', text: 'Vui lòng chọn ít nhất một loại thông báo, hoặc chọn "Tất cả"' });
      return;
    }

    setSaving(true);
    try {
      if (editingId === null) {
        const { error: insertErr } = await supabase.from('telegram_accounts').insert({
          user_email: 'admin@chin.vn',
          name: name.trim(),
          chat_id: chatId.trim(),
          bot_token: botToken.trim(),
          notify_types: notifyTypes,
          is_active: true,
        });
        if (insertErr) throw insertErr;
        setMsg({ type: 'success', text: `Đã thêm tài khoản "${name.trim()}"` });
      } else {
        const { error: updateErr } = await supabase
          .from('telegram_accounts')
          .update({
            name: name.trim(),
            chat_id: chatId.trim(),
            bot_token: botToken.trim(),
            notify_types: notifyTypes,
          })
          .eq('id', editingId);
        if (updateErr) throw updateErr;
        setMsg({ type: 'success', text: `Đã cập nhật tài khoản "${name.trim()}"` });
      }

      await loadAccounts();
      closeForm();
      setMsg(null);
    } catch (e2) {
      const m = e2 && typeof e2 === 'object' && 'message' in e2 ? String((e2 as Record<string, unknown>).message) : 'Failed to save';
      setMsg({ type: 'error', text: m });
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete ────────────────────────────────────────────────

  const handleDelete = async (id: number) => {
    try {
      const { error: delErr } = await supabase.from('telegram_accounts').delete().eq('id', id);
      if (delErr) throw delErr;
      setDeleteTarget(null);
      await loadAccounts();
    } catch (e) {
      const m = e && typeof e === 'object' && 'message' in e ? String((e as Record<string, unknown>).message) : 'Failed to delete';
      setMsg({ type: 'error', text: m });
    }
  };

  // ─── Toggle active/inactive ────────────────────────────────

  const handleToggle = async (acc: TelegramAccount) => {
    if (toggleBusy !== null) return;
    setToggleBusy(acc.id);
    try {
      const { error: updErr } = await supabase
        .from('telegram_accounts')
        .update({ is_active: !acc.is_active })
        .eq('id', acc.id);
      if (updErr) throw updErr;
      setAccounts((prev) => prev.map((a) => (a.id === acc.id ? { ...a, is_active: !a.is_active } : a)));
    } catch (e) {
      const m = e && typeof e === 'object' && 'message' in e ? String((e as Record<string, unknown>).message) : 'Failed to toggle';
      setMsg({ type: 'error', text: m });
    } finally {
      setToggleBusy(null);
    }
  };

  // ─── Test a specific account ───────────────────────────────

  const handleTest = async (acc: TelegramAccount) => {
    if (testState[acc.id]?.running) return;
    setTestState((prev) => ({ ...prev, [acc.id]: { running: true, result: null } }));

    const result = await sendTelegram(
      `🧪 <b>Test kết nối Telegram</b>\nTài khoản: ${acc.name}\nChat ID: ${acc.chat_id}\nThời gian: ${new Date().toLocaleString('vi-VN')}`,
      {
        chatIdOverride: acc.chat_id,
        botTokenOverride: acc.bot_token,
        dedupKey: `test:${acc.id}:${Date.now()}`,
      },
    );

    setTestState((prev) => ({
      ...prev,
      [acc.id]: {
        running: false,
        result: result.success ? 'ok' : (result.error || 'failed'),
      },
    }));
  };

  // ─── Render ────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bg-background-100 rounded-xl border border-background-200/70 p-6 flex items-center justify-center">
        <div className="flex items-center gap-2 text-foreground-500 text-sm">
          <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          Đang tải tài khoản Telegram...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-secondary-500/10 flex items-center justify-center">
            <i className="ri-telegram-line text-secondary-400 text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground-900">Telegram Accounts</h3>
            <p className="text-foreground-500 text-xs mt-0.5">
              {accounts.length > 0
                ? `${accounts.length} tài khoản — mỗi tài khoản nhận các loại thông báo được chọn`
                : 'Chưa có tài khoản nào — thêm tài khoản đầu tiên bên dưới'}
            </p>
          </div>
        </div>
        {!formOpen && (
          <button
            type="button"
            onClick={openAdd}
            className="px-4 py-2 bg-secondary-500 hover:bg-secondary-600 text-background-50 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-1.5"></i>
            Thêm tài khoản
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg px-3 py-2 text-xs flex items-start gap-2 bg-red-500/10 border border-red-500/20 text-red-400">
          <i className="ri-error-warning-line text-sm shrink-0 mt-px"></i>
          <span>{error}</span>
        </div>
      )}

      {/* Account list */}
      {accounts.length > 0 && !formOpen && (
        <div className="space-y-2">
          {accounts.map((acc) => {
            const isDeleting = deleteTarget === acc.id;
            const isBusy = toggleBusy === acc.id;
            const test = testState[acc.id];

            return (
              <div
                key={acc.id}
                className={`bg-background-50 rounded-lg border transition-all ${
                  acc.is_active ? 'border-background-200/70 hover:border-background-300/60' : 'border-background-200/50 opacity-70'
                }`}
              >
                {isDeleting ? (
                  <div className="p-3 flex items-center justify-between">
                    <span className="text-red-400 text-xs font-medium">Xóa tài khoản này?</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleDelete(acc.id)}
                        className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-background-50 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                      >
                        Xóa
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(null)}
                        className="px-3 py-1.5 bg-background-200/70 hover:bg-background-300/60 text-foreground-600 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                      >
                        Hủy
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-md bg-secondary-500/10 flex items-center justify-center shrink-0">
                          <i className="ri-telegram-line text-secondary-400 text-xs"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-foreground-900 text-sm font-medium">{acc.name}</p>
                            {!acc.is_active && (
                              <span className="px-1.5 py-0.5 rounded-full bg-background-200/70 text-foreground-500 text-[10px] font-medium">
                                Tạm ngưng
                              </span>
                            )}
                          </div>
                          <p className="text-foreground-500 text-xs mt-0.5">
                            Chat ID: <span className="font-medium text-foreground-700">{acc.chat_id}</span>
                            {' · '}Token: <span className="font-mono">{maskToken(acc.bot_token)}</span>
                          </p>
                          {/* Category badges */}
                          <div className="flex items-center gap-1 flex-wrap mt-2">
                            {acc.notify_types.includes('all') ? (
                              <span className="px-2 py-0.5 rounded-full bg-accent-500/10 text-accent-400 text-[10px] font-medium">
                                Tất cả thông báo
                              </span>
                            ) : (
                              acc.notify_types.map((code) => (
                                <span
                                  key={code}
                                  className="px-2 py-0.5 rounded-full bg-secondary-500/10 text-secondary-400 text-[10px] font-medium"
                                >
                                  {CATEGORY_LABELS[code] || code}
                                </span>
                              ))
                            )}
                            {acc.notify_types.length === 0 && (
                              <span className="text-foreground-400 text-[10px] italic">Chưa chọn loại thông báo</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => handleTest(acc)}
                          disabled={test?.running}
                          className="h-7 px-2 rounded-md flex items-center justify-center gap-1 text-foreground-400 hover:text-secondary-400 hover:bg-secondary-500/10 transition-all cursor-pointer text-[11px] disabled:opacity-50 whitespace-nowrap"
                          title="Gửi tin test"
                        >
                          {test?.running ? (
                            <i className="ri-loader-4-line text-xs animate-spin"></i>
                          ) : (
                            <i className="ri-send-plane-line text-xs"></i>
                          )}
                          {test?.result === 'ok' && <span className="text-primary-400">OK</span>}
                          {test?.result && test.result !== 'ok' && <span className="text-red-400" title={test.result}>Lỗi</span>}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggle(acc)}
                          disabled={isBusy}
                          className={`h-7 w-7 rounded-md flex items-center justify-center transition-all cursor-pointer disabled:opacity-50 ${
                            acc.is_active
                              ? 'text-primary-400 hover:bg-primary-500/10'
                              : 'text-foreground-400 hover:bg-background-200/70'
                          }`}
                          title={acc.is_active ? 'Tạm ngưng' : 'Kích hoạt'}
                        >
                          <i className={`${acc.is_active ? 'ri-toggle-fill' : 'ri-toggle-line'} text-base`}></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => openEdit(acc)}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-400 hover:bg-accent-500/10 transition-all cursor-pointer"
                          title="Sửa"
                        >
                          <i className="ri-edit-line text-xs"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(acc.id)}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-red-400 hover:bg-red-500/10 transition-all cursor-pointer"
                          title="Xóa"
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit form */}
      {formOpen && (
        <div className="border-t border-background-200/70 pt-4 mt-4">
          <h4 className="text-xs font-heading font-semibold text-foreground-800 mb-3">
            {editingId === null ? 'Thêm tài khoản Telegram mới' : 'Chỉnh sửa tài khoản'}
          </h4>

          <form onSubmit={handleSave}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Tên nhãn</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setMsg(null); }}
                  placeholder="VD: Nhóm VIP, Bot tín hiệu chính..."
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-secondary-500/50 focus:ring-1 focus:ring-secondary-500/30 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Chat ID</label>
                <input
                  type="text"
                  value={chatId}
                  onChange={(e) => { setChatId(e.target.value); setMsg(null); }}
                  placeholder="e.g. -1001234567890"
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-secondary-500/50 focus:ring-1 focus:ring-secondary-500/30 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Bot Token</label>
                <input
                  type="text"
                  value={botToken}
                  onChange={(e) => { setBotToken(e.target.value); setMsg(null); }}
                  placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-secondary-500/50 focus:ring-1 focus:ring-secondary-500/30 transition-all"
                />
                <p className="text-foreground-400 text-[11px] mt-1">Lấy từ @BotFather trên Telegram</p>
              </div>
            </div>

            {/* Category selection */}
            <div className="mt-4">
              <label className="block text-xs font-medium text-foreground-600 mb-2">Loại thông báo nhận được</label>

              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-accent-500/8 border border-accent-500/20 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  className="w-4 h-4 rounded accent-accent-500 cursor-pointer"
                />
                <span className="text-xs font-medium text-foreground-900">Tất cả thông báo</span>
              </label>

              {!allSelected && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {CATEGORY_OPTIONS.map((cat) => (
                    <label
                      key={cat.code}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-background-50 border border-background-300/60 cursor-pointer hover:border-secondary-500/40 transition-all"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTypes.includes(cat.code)}
                        onChange={() => toggleCategory(cat.code)}
                        className="w-4 h-4 rounded accent-secondary-500 cursor-pointer"
                      />
                      <span className="text-xs text-foreground-700">{cat.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {msg && (
              <div className={`mt-4 rounded-lg px-3 py-2 text-xs flex items-start gap-2 ${
                msg.type === 'success'
                  ? 'bg-primary-500/10 border border-primary-500/20 text-primary-400'
                  : 'bg-red-500/10 border border-red-500/20 text-red-400'
              }`}>
                <i className={`${msg.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} text-sm shrink-0 mt-px`}></i>
                <span>{msg.text}</span>
              </div>
            )}

            <div className="mt-4 flex items-center gap-2">
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2.5 bg-secondary-500 hover:bg-secondary-600 text-background-50 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {saving ? 'Đang lưu...' : (editingId === null ? 'Thêm tài khoản' : 'Lưu thay đổi')}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="px-5 py-2.5 bg-background-200/70 hover:bg-background-300/60 text-foreground-600 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                Hủy
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}